import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  customEmailValidator,
  ibanValidator,
  sortCodeValidator,
  specialCharacterValidator,
  VALIDATOR_NOT_ONLY_NUMBERS,
  VALIDATOR_ONLY_SPECIAL_CHARACTERS,
} from '../../../shared/validators/custom-validators.validator';
import { VALIDATOR_ONLY_SPACES } from '../../../shared/constants/helpers.const';

@Injectable({ providedIn: 'root' })
export class Add_Debtor_Creditor_FormProvider {
  form: FormGroup;

  constructor(private fb: FormBuilder) {}

  initForm(data?: any): FormGroup {
    return (this.form = this.fb.group(
      {
        //main block controls
        type: [data?.type || '', [Validators.required]],
        organisationName: [data?.organisationName],
        firstName: [
          data?.firstName || '',
          [
            VALIDATOR_ONLY_SPACES,
            VALIDATOR_ONLY_SPECIAL_CHARACTERS,
            Validators.maxLength(125),
          ],
        ],
        lastName: [
          data?.lastName || '',
          [
            VALIDATOR_ONLY_SPACES,
            VALIDATOR_ONLY_SPECIAL_CHARACTERS,
            Validators.maxLength(125),
          ],
        ],
        tradeName: [data?.tradeName || ''],
        classificationId: [data?.classificationId || ''],
        previousName: [data?.previousName || ''],

        name: [data?.name || ''],
        isPrevious: [false],

        // bank controls
        ccy: [data?.ccy || '', [Validators.required]],
        bankName: [data?.bankName || ''],
        account: [
          data?.account || '',
          [Validators.minLength(8), Validators.maxLength(50)],
        ],
        sortCode: [data?.sortCode || '', [sortCodeValidator()]],
        swiftBic: [
          data?.swiftBic || '',
          [
            VALIDATOR_ONLY_SPACES,
            VALIDATOR_ONLY_SPECIAL_CHARACTERS,
            Validators.maxLength(250),
          ],
        ],
        iban: [
          data?.iban || '',
          [
            VALIDATOR_ONLY_SPACES,
            VALIDATOR_ONLY_SPECIAL_CHARACTERS,
            ibanValidator(),
          ],
        ],
        bankId: [data?.bankId || ''],

        //contact controls
        email: [data?.email || '', [customEmailValidator()]],
        phoneNumbers: this.fb.group({
          home: this.#createPhoneGroup(),
          office: this.#createPhoneGroup(),
          mobile: this.#createPhoneGroup(),
        }),

        //addresses controls
        addresses: this.fb.array([
          this.#createAddress('Primary', data),
          this.#createAddress('Billing', data),
        ]),

        // other controls
        loyalSince: [data?.loyalSince || ''],
        totalHistoricValue: [data?.totalHistoricValue || ''],
        paymentCycle: [data?.paymentCycle || ''],
        creditNotes: [data?.creditNotes || ''],
        invoicesPredicted: [data?.invoicesPredicted || ''],
        predictedRevenue: [data?.predictedRevenue || ''],
        experienceFile: [data?.experienceFile || null],
      },
      {
        validators: [
          ibanOrAccountRequired(),
          atLeastOnePhoneRequired(),
          createPhoneValidator('mobilePhoneIncomplete', [
            'phoneNumbers.mobile.countryCode',
            'phoneNumbers.mobile.number',
          ]),
          createPhoneValidator('homePhoneIncomplete', [
            'phoneNumbers.home.countryCode',
            'phoneNumbers.home.number',
          ]),
          createPhoneValidator('officePhoneIncomplete', [
            'phoneNumbers.office.countryCode',
            'phoneNumbers.office.number',
          ]),
        ],
      },
    ));
  }

  #createAddress(type: 'Primary' | 'Billing', data: any): FormGroup {
    return this.fb.group({
      country: [data?.country || 'Ireland', [Validators.required]],
      countryCode: [data?.countryCode || 'IE', [Validators.required]],
      lineOne: [
        data?.lineOne || '',
        [
          Validators.required,
          VALIDATOR_ONLY_SPACES,
          VALIDATOR_ONLY_SPECIAL_CHARACTERS,
          specialCharacterValidator(/[A-Za-z0-9\-_ ]/),
          Validators.maxLength(250),
        ],
      ],
      lineTwo: [data?.lineTwo || ''],
      area: [data?.area || ''],
      city: [
        data?.city || '',
        [
          Validators.required,
          VALIDATOR_ONLY_SPACES,
          VALIDATOR_ONLY_SPECIAL_CHARACTERS,
          VALIDATOR_NOT_ONLY_NUMBERS,
          Validators.maxLength(250),
        ],
      ],
      state: [
        data?.state || '',
        [
          Validators.required,
          VALIDATOR_ONLY_SPACES,
          VALIDATOR_ONLY_SPECIAL_CHARACTERS,
          specialCharacterValidator(/[A-Za-z0-9\-_ ]/),
          Validators.maxLength(250),
        ],
      ],
      zipCode: [
        data?.zipCode || '',
        [
          Validators.required,
          VALIDATOR_ONLY_SPACES,
          VALIDATOR_ONLY_SPECIAL_CHARACTERS,
          specialCharacterValidator(/[A-Za-z0-9\-_ ]/),
          Validators.maxLength(250),
        ],
      ],
      type: [type],
    });
  }

  #createPhoneGroup(): FormGroup {
    return this.fb.group({
      countryCode: [''],
      number: [''],
      extension: [''],
    });
  }
}

export const createPhoneValidator = (
  errorKey: string,
  fieldNames: string[],
): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const values = fieldNames.map((field) => control.get(field)?.value);

    const anyFilled = values.some((value) => !!value);
    const allFilled = values.every((value) => !!value);

    return !anyFilled || allFilled ? null : { [errorKey]: true };
  };
};

export const atLeastOnePhoneRequired = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const mobileNumber = control.get('phoneNumbers.mobile.number')?.value;
    const homeNumber = control.get('phoneNumbers.home.number')?.value;
    const officeNumber = control.get('phoneNumbers.office.number')?.value;

    const hasAtLeastOne = !!(mobileNumber || homeNumber || officeNumber);
    return hasAtLeastOne ? null : { atLeastOnePhoneRequired: true };
  };
};

export const ibanOrAccountRequired = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const iban = control.get('iban')?.value;
    const account = control.get('account')?.value;
    return iban || account ? null : { ibanOrAccountRequired: true };
  };
};
